import React from 'react';
import { AlertCircle } from 'react-feather';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import SessionDetails from 'components/InPersonTraining/shared/SessionDetails';
import RadioFieldGroup from 'components/shared/RadioFieldGroup/index';

import RadioField from 'components/shared/RadioFieldGroup/RadioField';
import CalendarGraphic from 'components/InPersonTraining/shared/CalendarGraphic';
import moment, { ISO_8601, now } from 'moment';
import { isMobileDevice } from 'utils/browserCheck';

const SessionListing = ({
  sessions,
  selectedSessionId,
  handleSessionClick,
  hasMultipleSessions,
  attendance,
  currentSession,
  event,
  changingAttendance,
  previewing,
  canChangeAttendance,
  pastEvent,
  alwaysShowMeetingUrl = false,
}) => {
  const missingAttendanceStatus =
    attendance.rsvp_status === null && attendance.did_attend === null;
  const missingSessionId = attendance.session_id === null;

  const rsvpConfirmed =
    !changingAttendance &&
    !isEmpty(attendance) &&
    !missingSessionId &&
    !missingAttendanceStatus;

  const positiveRsvp = ['will_attend', 'did_attend'].includes(
    attendance.status,
  );

  const showMultiSessionListing =
    hasMultipleSessions && (!rsvpConfirmed || !positiveRsvp || previewing);
  const showMultiSessionDetail =
    hasMultipleSessions &&
    event.rsvp_type === 'single' &&
    attendance.session_id &&
    positiveRsvp &&
    !previewing &&
    !changingAttendance;
  const isSingleRsvpType =
    showMultiSessionListing && event.rsvp_type === 'single';
  const isAllRsvpType = event.rsvp_type === 'all';
  const showMeetingUrl =
    (positiveRsvp && !changingAttendance) || alwaysShowMeetingUrl;

  const disableSessionButtons =
    previewing ||
    (attendance.rsvp_status === 'will_not_attend' &&
      changingAttendance === false);
  const disableMeetingLink = !canChangeAttendance && pastEvent;
  return (
    <>
      {showMultiSessionDetail && (
        <SessionDetails
          session={currentSession}
          eventId={event.id}
          showRegistrationCap
          showVideoMeetingText={isEmpty(attendance)}
          hasMultipleSessions={false}
          previewing={previewing}
          disableMeetingLink={disableMeetingLink}
          showSessionName
        />
      )}

      {isAllRsvpType &&
        sessions.map((session, index) => {
          const showBreak = index < sessions.length - 1;
          const disablePastSession =
            moment(session.start_time, ISO_8601).isBefore(now()) &&
            !canChangeAttendance;
          return (
            <div key={session.id} className="d-f fd-c">
              <div className="MySessionListCard ai-c">
                <CalendarGraphic isLocalTime isRsvpPage session={session} />
                <div>
                  <Text className="d-f mb-4" variant={TEXT_VARIANTS.BODY_BOLD}>
                    {session.name}
                  </Text>
                  <SessionDetails
                    session={session}
                    eventId={event.id}
                    showRegistrationCap={false}
                    hasMultipleSessions={false}
                    previewing={
                      previewing || disableSessionButtons || disablePastSession
                    }
                    showVideoMeetingText={
                      !showMeetingUrl &&
                      t(
                        'in_person_training.event_overview.video_conference_link.available_for_attendees',
                      )
                    }
                    showVideoMeetingUrl={showMeetingUrl}
                    disableMeetingLink={disableMeetingLink}
                  />
                </div>
              </div>
              <div>{showBreak && <hr className="mt-24 mb-24" />}</div>
            </div>
          );
        })}

      {isSingleRsvpType && !showMultiSessionDetail && (
        <RadioFieldGroup
          name="selectedSession"
          selectedValue={selectedSessionId}
        >
          {sessions.map(session => {
            const sessionAtCapacity = session.max_attendees
              ? session.max_attendees <= session.total_registered
              : false;
            const disablePastSession =
              moment(session.start_time, ISO_8601).isBefore(now()) &&
              !canChangeAttendance;
            return (
              <button
                className={classNames('EventSessionListing pos-r py-16 px-24', {
                  'EventSessionListing--selected':
                    session.id === selectedSessionId,
                })}
                onClick={() => handleSessionClick(session.id)}
                key={session.id}
                type="button"
                disabled={
                  sessionAtCapacity ||
                  disablePastSession ||
                  disableSessionButtons
                }
              >
                <div className="SelectableSession">
                  <RadioField
                    readOnly
                    checked={session.id === selectedSessionId}
                    name="selectedSession"
                    value={session.id}
                    className="SelectableSessionRadio d-g mb-4"
                    disabled={sessionAtCapacity}
                  />
                  <div className="SelectableSessionName mt-3 mb-4">
                    <Text
                      variant={TEXT_VARIANTS.BODY_BOLD}
                      className={
                        isMobileDevice() === false
                          ? 'SelectableSessionName-truncated'
                          : ''
                      }
                    >
                      {session.name}
                    </Text>
                    {sessionAtCapacity && (
                      <span className="SelectableSession-capacity desktop mb-4">
                        <div className="d-f">
                          <AlertCircle
                            size={15}
                            className="SelectableSession-icon"
                          />
                          {t(
                            'in_person_training.events.register.session_at_capacity',
                          )}
                        </div>
                      </span>
                    )}
                  </div>
                  <div className="SelectableSessionDetails">
                    <SessionDetails
                      session={session}
                      eventId={session.event_id}
                      hasMultipleSessions={hasMultipleSessions}
                      previewing={
                        previewing ||
                        disableSessionButtons ||
                        disablePastSession ||
                        session.type?.at_capacity
                      }
                      showRegistrationCap={false}
                      showVideoMeetingText={!showMeetingUrl}
                      showVideoMeetingUrl={showMeetingUrl}
                    />
                    {sessionAtCapacity && (
                      <span className="SelectableSession-capacity mobile">
                        <div className="d-f">
                          <AlertCircle
                            size={15}
                            className="SelectableSession-icon"
                          />
                          <span className="SelectableSession-capacity mobile large">
                            {t(
                              'in_person_training.events.register.session_at_capacity',
                            )}
                          </span>
                          <span className="SelectableSession-capacity mobile small">
                            {t(
                              'in_person_training.events.register.at_capacity',
                            )}
                          </span>
                        </div>
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </RadioFieldGroup>
      )}
    </>
  );
};

export default SessionListing;
